import { createColumnHelper, type CellContext } from '@tanstack/react-table';
import { MoreHorizontalIcon, PlusIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import type { CarrierDTO } from '@invenco/shipping-interface';

import {
  AlertDialogWithAction,
  AlertDialogWithActionCancel,
  AlertDialogWithActionContent,
  AlertDialogWithActionDescription,
  AlertDialogWithActionFooter,
  AlertDialogWithActionHeader,
  AlertDialogWithActionSubmit,
  AlertDialogWithActionTitle,
} from '@/components/alert-dialog-with-action';
import { Button } from '@/components/core/button';
import { Card, CardAction, CardHeader } from '@/components/core/card';
import { DialogTrigger } from '@/components/core/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/core/dropdown-menu';
import {
  DataTable,
  DataTableCardBody,
  DataTableCardTitle,
  useDataTable,
  useDataTableState,
} from '@/components/data-table';
import { FormDialog } from '@/components/forms/form-dialog';
import type { Result } from '@/shared/helpers/Result';

import { CreateSurchargeDialogContent } from './create-surcharge-dialog-content';
import {
  computeSurchargeStatuses,
  surchargeColumns,
  surchargeGroupKey,
  type SurchargeRow,
} from './surcharge-columns';
import {
  UpdateSurchargeRateDialogContent,
  type UpdateSurchargeRateOutput,
} from './update-surcharge-rate-dialog-content';
import type { CarrierSurchargesData } from './use-carrier-surcharges';

type Props = {
  /** The carrier whose surcharges are shown — its services name the service-scoped rows. */
  carrier?: CarrierDTO;
  surcharges: CarrierSurchargesData['surcharges'];
  isLoading: boolean;
  hasError: boolean;
  createSurcharge: CarrierSurchargesData['createSurcharge'];
  deleteSurcharge: CarrierSurchargesData['deleteSurcharge'];
};

function SurchargeRowActions({
  surcharge,
  onSupersede,
  onDelete,
}: {
  surcharge: SurchargeRow;
  onSupersede: (surcharge: SurchargeRow, data: UpdateSurchargeRateOutput) => Promise<Result>;
  onDelete: (surchargeId: string) => Promise<Result>;
}) {
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="Open surcharge actions">
            <MoreHorizontalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => setIsUpdateOpen(true)}>Update</DropdownMenuItem>
          <DropdownMenuItem variant="destructive" onSelect={() => setIsDeleteOpen(true)}>
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <FormDialog open={isUpdateOpen} onOpenChange={setIsUpdateOpen}>
        <UpdateSurchargeRateDialogContent
          surcharge={surcharge}
          onSubmit={(data) => onSupersede(surcharge, data)}
        />
      </FormDialog>
      <AlertDialogWithAction
        open={isDeleteOpen}
        onOpenChange={setIsDeleteOpen}
        onAction={() => onDelete(surcharge.id)}
      >
        <AlertDialogWithActionContent>
          <AlertDialogWithActionHeader>
            <AlertDialogWithActionTitle>Delete surcharge?</AlertDialogWithActionTitle>
            <AlertDialogWithActionDescription>
              {surcharge.hasOtherVersions
                ? `This deletes only this version of "${surcharge.name}" — its other versions remain.`
                : `This deletes the surcharge "${surcharge.name}".`}
            </AlertDialogWithActionDescription>
          </AlertDialogWithActionHeader>
          <AlertDialogWithActionFooter>
            <AlertDialogWithActionCancel>Cancel</AlertDialogWithActionCancel>
            <AlertDialogWithActionSubmit>Delete</AlertDialogWithActionSubmit>
          </AlertDialogWithActionFooter>
        </AlertDialogWithActionContent>
      </AlertDialogWithAction>
    </>
  );
}

const columnHelper = createColumnHelper<SurchargeRow>();

const createSurchargeActionsCell = (
  onSupersede: (surcharge: SurchargeRow, data: UpdateSurchargeRateOutput) => Promise<Result>,
  onDelete: (surchargeId: string) => Promise<Result>,
) =>
  function SurchargeActionsCell({ row }: CellContext<SurchargeRow, unknown>) {
    return (
      <SurchargeRowActions surcharge={row.original} onSupersede={onSupersede} onDelete={onDelete} />
    );
  };

/**
 * The surcharges configured for a carrier — carrier-wide and service-scoped rows in one table,
 * including scheduled and superseded versions. Rows are immutable on the backend, so the table is
 * read-only: "Update rate" supersedes (creates a new version) and Delete removes one version.
 */
export function SurchargesCard({
  carrier,
  surcharges,
  isLoading,
  hasError,
  createSurcharge,
  deleteSurcharge,
}: Props) {
  const serviceNameById = useMemo(
    () =>
      new Map(
        (carrier?.carrierServices ?? []).map((service) => [
          service.id,
          service.name || service.code || service.id,
        ]),
      ),
    [carrier?.carrierServices],
  );

  const rows: SurchargeRow[] = useMemo(() => {
    const list = surcharges ?? [];
    const statuses = computeSurchargeStatuses(list);
    const groupCounts = new Map<string, number>();
    list.forEach((surcharge) => {
      const key = surchargeGroupKey(surcharge);
      groupCounts.set(key, (groupCounts.get(key) ?? 0) + 1);
    });

    return [...list]
      .sort((a, b) => a.name.localeCompare(b.name) || b.validFrom.localeCompare(a.validFrom))
      .map((surcharge) => ({
        ...surcharge,
        appliesTo:
          surcharge.carrierServiceId === null
            ? 'All services'
            : (serviceNameById.get(surcharge.carrierServiceId) ?? surcharge.carrierServiceId),
        status: statuses.get(surcharge.id) ?? 'ACTIVE',
        hasOtherVersions: (groupCounts.get(surchargeGroupKey(surcharge)) ?? 1) > 1,
      }));
  }, [surcharges, serviceNameById]);

  const handleSupersede = useCallback(
    (surcharge: SurchargeRow, { rate, validFrom }: UpdateSurchargeRateOutput) =>
      createSurcharge({
        name: surcharge.name,
        ...(surcharge.carrierServiceId
          ? { carrierServiceId: surcharge.carrierServiceId }
          : // The '' fallback is unreachable: a carrier-scoped row carries its carrierId (XOR contract).
            { carrierId: surcharge.carrierId ?? carrier?.id ?? '' }),
        chargeMethod: surcharge.chargeMethod,
        rate,
        ...(surcharge.applicability && { applicability: surcharge.applicability }),
        ...(validFrom && { validFrom }),
      }),
    [createSurcharge, carrier?.id],
  );

  const columns = useMemo(
    () => [
      ...surchargeColumns,
      columnHelper.display({
        id: 'actions',
        cell: createSurchargeActionsCell(handleSupersede, deleteSurcharge),
        meta: { align: 'right' },
      }),
    ],
    [handleSupersede, deleteSurcharge],
  );

  const { state, onSortingChange } = useDataTableState({
    columns,
    tableId: 'carrier-details-surcharges',
  });

  const table = useDataTable({
    data: rows,
    columns,
    state,
    isLoading,
    hasError,
    manualPagination: true,
    manualSorting: false,
    loadingRowCount: 3,
    onSortingChange,
  });

  return (
    <Card aria-label="Surcharges">
      <CardHeader>
        <DataTableCardTitle table={table}>Surcharges</DataTableCardTitle>
        <CardAction>
          <FormDialog>
            <DialogTrigger asChild>
              <Button size="sm" variant="ghost" disabled={isLoading || !carrier}>
                <PlusIcon />
                New surcharge
              </Button>
            </DialogTrigger>
            <CreateSurchargeDialogContent carrier={carrier} onSubmit={createSurcharge} />
          </FormDialog>
        </CardAction>
      </CardHeader>
      <DataTableCardBody table={table} emptyMessage="No surcharges configured for this carrier yet">
        <DataTable table={table} />
      </DataTableCardBody>
    </Card>
  );
}
